/**
 * Frontend Error Reporter (Story 03-03)
 *
 * Ships uncaught errors and React-boundary catches to `POST /api/errors`.
 * **Governing constraint: this module can never harm the game it observes.**
 * Every public entry point is wrapped in a swallowing try/catch — a reporter
 * that throws inside `window.onerror` produces an infinite loop, which is the
 * most common failure mode of hand-rolled reporters.
 *
 * Design (see story for the full rationale):
 * - Fingerprint = hash(message + top stack frame only) — full-stack hashing
 *   defeats dedupe because minified frames vary by chunk hash across deploys.
 * - A fingerprint is reported **once per session**; further occurrences just
 *   bump a counter. A batch is drained and marked "sent" the instant a flush
 *   is attempted — so a failed transmission is dropped, never retried.
 * - At most `ERROR_LOG_MAX_PER_SESSION` distinct fingerprints are ever
 *   tracked; anything beyond that is dropped silently.
 * - `sendBeacon` on `pagehide` (not `unload` — unreliable on mobile Safari
 *   and blocks bfcache), plain `fetch` on the flush interval / visibilitychange.
 * - No `userId`, no hash of one, no IP — `sessionId` is a random per-page-load value.
 */

import type { ErrorReport, ErrorReportBatch, ErrorReportConnectionStatus } from 'shared-types';
import { peekAblyConnectionState } from './ably-client';
import {
  ERROR_LOG_FLUSH_MS,
  ERROR_LOG_MAX_PER_SESSION,
  ERROR_LOG_MAX_STACK_BYTES,
  ERROR_LOG_MAX_BATCH_BYTES
} from '@/config/error-reporting';

/** Schema caps mirrored from `error-log.schemas.ts` (`.strict()` rejects anything longer/extra). */
const MAX_MESSAGE_LEN = 2000;
const MAX_ROUTE_LEN = 500;
const MAX_APP_VERSION_LEN = 50;

interface TrackedError {
  report: ErrorReport;
  /** Set the instant a flush is attempted for this fingerprint — never retried after. */
  sent: boolean;
}

interface GameContext {
  gameId?: string;
  turnEpoch?: number;
}

function generateSessionId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to the non-crypto fallback below
  }
  return `sid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** Random per-page-load value. NOT derived from or linkable to a userId. */
export const sessionId: string = generateSessionId();

const trackedErrors = new Map<string, TrackedError>();
const pendingFingerprints: string[] = [];
let gameContext: GameContext = {};
let installed = false;

/**
 * Cheap non-cryptographic hash (djb2) — deterministic, fast, no dependency.
 */
function hashString(input: string): string {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 33 + input.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
}

/**
 * The top stack frame only — including lower frames defeats dedupe, since
 * minified frames vary by chunk hash across deploys.
 */
function getTopFrame(stack: string): string {
  const lines = stack.split('\n').map((line) => line.trim());
  const [firstLine, ...rest] = lines;
  const frameLine = rest.find((line) => line.length > 0);
  return frameLine ?? firstLine;
}

/** Dedupe key: hash(message + top stack frame). Exported for direct unit testing. */
export function fingerprint(message: string, stack: string): string {
  return hashString(`${message}|${getTopFrame(stack)}`);
}

function normalizeError(error: unknown): { message: string; stack: string } {
  try {
    if (error instanceof Error) {
      const message =
        typeof error.message === 'string' && error.message.length > 0
          ? error.message
          : 'Unknown error';
      const stack =
        typeof error.stack === 'string' && error.stack.length > 0 ? error.stack : message;
      return { message, stack };
    }
    if (typeof error === 'string' && error.length > 0) {
      return { message: error, stack: error };
    }
    return { message: 'Unknown error', stack: 'Unknown error' };
  } catch {
    return { message: 'Unknown error', stack: 'Unknown error' };
  }
}

function truncateStack(stack: string, maxBytes: number): string {
  try {
    const bytes = new TextEncoder().encode(stack);
    if (bytes.length <= maxBytes) return stack;
    return new TextDecoder().decode(bytes.slice(0, maxBytes));
  } catch {
    return stack.length > maxBytes ? stack.slice(0, maxBytes) : stack;
  }
}

function getRoute(): string {
  try {
    const path = window.location.pathname || '/';
    return path.length > MAX_ROUTE_LEN ? path.slice(0, MAX_ROUTE_LEN) : path;
  } catch {
    return '/';
  }
}

function getAppVersion(): string {
  try {
    const version = import.meta.env.VITE_APP_VERSION;
    return typeof version === 'string' && version.length > 0
      ? version.slice(0, MAX_APP_VERSION_LEN)
      : 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Point-in-time connection status. Deliberately reads `peekAblyConnectionState`
 * (never `getAblyClient`) — checking connection health must never itself
 * trigger opening a new Ably connection as a side effect of crash reporting.
 */
function getConnectionStatus(): ErrorReportConnectionStatus {
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return 'offline';
    const state = peekAblyConnectionState();
    if (state === null) return 'connected';
    if (state === 'connected') return 'connected';
    if (state === 'connecting') return 'connecting';
    if (state === 'disconnected' || state === 'suspended' || state === 'failed') {
      return 'disconnected';
    }
    return 'connected';
  } catch {
    return 'connected';
  }
}

function getApiUrl(): string {
  try {
    return import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
  } catch {
    return 'http://localhost:3000/api';
  }
}

/**
 * Set the current game context (gameId / turnEpoch) so subsequent reports
 * carry it. Call with `{}` (or omit fields) when no game is in progress.
 */
export function setGameContext(context: GameContext): void {
  try {
    gameContext = { gameId: context.gameId, turnEpoch: context.turnEpoch };
  } catch {
    // never throw
  }
}

/**
 * Report an error caught by a global hook or a React error boundary.
 * Fingerprints, dedupes (bumping `occurrences` for repeats), enforces the
 * per-session cap, truncates the stack, and enqueues for the next flush.
 * Never throws.
 */
export function reportError(error: unknown, component: string): void {
  try {
    const { message, stack } = normalizeError(error);
    const fp = fingerprint(message, stack);

    const existing = trackedErrors.get(fp);
    if (existing) {
      if (!existing.sent) existing.report.occurrences += 1;
      return;
    }

    if (trackedErrors.size >= ERROR_LOG_MAX_PER_SESSION) {
      return; // session cap reached — drop silently
    }

    const report: ErrorReport = {
      fingerprint: fp,
      message: message.slice(0, MAX_MESSAGE_LEN),
      stack: truncateStack(stack, ERROR_LOG_MAX_STACK_BYTES),
      component,
      route: getRoute(),
      gameId: gameContext.gameId,
      turnEpoch: gameContext.turnEpoch,
      connectionStatus: getConnectionStatus(),
      appVersion: getAppVersion(),
      sessionId,
      timestamp: Date.now(),
      occurrences: 1
    };

    trackedErrors.set(fp, { report, sent: false });
    pendingFingerprints.push(fp);
  } catch {
    // Never throw — the reporter must not become a second source of crashes.
  }
}

/** Drains the pending queue, marking every drained fingerprint as sent (never retried). */
function drainPendingBatch(): ErrorReport[] {
  const batch: ErrorReport[] = [];
  for (const fp of pendingFingerprints) {
    const entry = trackedErrors.get(fp);
    if (entry && !entry.sent) {
      entry.sent = true;
      batch.push(entry.report);
    }
  }
  pendingFingerprints.length = 0;
  return batch;
}

/** Returns the encoded JSON, or `null` if encoding failed or the batch exceeds the body limit. */
function encodeBatch(batch: ErrorReport[]): string | null {
  try {
    const payload: ErrorReportBatch = { errors: batch };
    const json = JSON.stringify(payload);
    const byteLength = new TextEncoder().encode(json).length;
    if (byteLength > ERROR_LOG_MAX_BATCH_BYTES) return null;
    return json;
  } catch {
    return null;
  }
}

function sendViaFetch(url: string, json: string): void {
  try {
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: json,
      keepalive: true
    }).catch(() => {
      // Never retry — a failed transmission is simply dropped.
    });
  } catch {
    // never throw
  }
}

function sendViaBeacon(url: string, json: string): void {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([json], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
    sendViaFetch(url, json);
  } catch {
    // never throw
  }
}

/**
 * Flush the pending batch. `useBeacon` selects `navigator.sendBeacon`
 * (used on `pagehide`, where a page in teardown cannot rely on `fetch`
 * completing); otherwise a normal `fetch` is used. Never blocks, never
 * throws, never retries a dropped/failed batch.
 */
export function flush(useBeacon = false): void {
  try {
    if (pendingFingerprints.length === 0) return;
    const batch = drainPendingBatch();
    if (batch.length === 0) return;

    const json = encodeBatch(batch);
    if (json === null) return; // dropped: encoding failed or exceeds the body limit

    const url = `${getApiUrl()}/errors`;
    if (useBeacon) {
      sendViaBeacon(url, json);
    } else {
      sendViaFetch(url, json);
    }
  } catch {
    // never throw
  }
}

/**
 * Installs the global `window.onerror` + `unhandledrejection` hooks, the
 * `visibilitychange` / `pagehide` flush triggers, and the flush interval.
 * Idempotent — safe to call more than once (installs only the first time).
 */
export function installGlobalErrorHandlers(): void {
  try {
    if (installed) return;
    installed = true;

    window.addEventListener('error', (event: ErrorEvent) => {
      try {
        reportError(event.error ?? event.message, 'window.onerror');
      } catch {
        // never throw
      }
    });

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      try {
        reportError(event.reason, 'unhandledrejection');
      } catch {
        // never throw
      }
    });

    document.addEventListener('visibilitychange', () => {
      try {
        if (document.visibilityState === 'hidden') flush(false);
      } catch {
        // never throw
      }
    });

    window.addEventListener('pagehide', () => {
      try {
        flush(true);
      } catch {
        // never throw
      }
    });

    setInterval(() => {
      flush(false);
    }, ERROR_LOG_FLUSH_MS);
  } catch {
    // never throw
  }
}
